import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminNav from "../components/AdminNav";
import RequireAdmin from "../components/RequireAdmin";
import {
  adminCacheStatus,
  adminCatalogStatus,
  adminRarityStatus,
  resetCacheErrors,
  triggerCatalogSync,
  triggerImageCache,
  triggerRarityEnrich,
} from "../lib/api";

export default function AdminCatalogPage() {
  const [cache, setCache] = useState(null);
  const [rarity, setRarity] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const [c, r, cat] = await Promise.all([
        adminCacheStatus(),
        adminRarityStatus(),
        adminCatalogStatus(),
      ]);
      setCache(c);
      setRarity(r);
      setCatalog(cat);
      setState("ready");
    } catch (e) { setState("error"); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const wrap = async (fn, msg) => {
    try { await fn(); toast(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <RequireAdmin>
      <div
        data-testid="admin-catalog"
        className="mx-auto w-full max-w-[1200px] px-5 py-8 md:px-gutter md:py-10"
      >
        <Breadcrumbs items={[
          { label: "Home", to: "/" },
          { label: "Admin", to: "/admin" },
          { label: "Catalog & cache" },
        ]} />
        <h1 className="mt-6 text-title-page text-ink-text">Catalog &amp; cache</h1>
        <AdminNav />

        {state === "loading" && !cache && <div className="text-body text-ink-muted">Loading…</div>}
        {state === "error" && <div className="text-body text-ink-muted">Failed to load.</div>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            testId="catalog-card-cache"
            title="Image cache"
            subtitle={cache && `${cache.totals.percent}%`}
            stats={cache && [
              { k: "Cached", v: cache.totals.cards_cached },
              { k: "Pending", v: cache.totals.cards_pending },
              { k: "Total cards", v: cache.totals.cards_total },
            ]}
            worker={cache?.worker}
            actions={
              <>
                <button
                  data-testid="catalog-cache-start"
                  onClick={() => wrap(triggerImageCache, "Image-cache worker started.")}
                  className="rounded-card border border-ink-chip bg-ink-surface px-3 py-1.5 text-meta text-ink-text hover:border-ink-muted"
                >
                  Start / resume
                </button>
                <button
                  data-testid="catalog-cache-reset-errors"
                  onClick={() => wrap(resetCacheErrors, "Errored rows re-queued.")}
                  className="rounded-card border border-ink-chip bg-ink-surface px-3 py-1.5 text-meta text-ink-muted hover:text-ink-text"
                >
                  Reset errors
                </button>
              </>
            }
          />
          <StatCard
            testId="catalog-card-rarity"
            title="Rarity enrichment"
            subtitle={rarity && `${rarity.totals.cards_with_rarity} / ${rarity.totals.cards_total}`}
            stats={rarity && [
              { k: "With rarity", v: rarity.totals.cards_with_rarity },
              { k: "Pending check", v: rarity.totals.cards_pending_check },
              { k: "Total", v: rarity.totals.cards_total },
            ]}
            worker={rarity?.worker}
            actions={
              <button
                data-testid="catalog-rarity-start"
                onClick={() => wrap(triggerRarityEnrich, "Rarity worker started.")}
                className="rounded-card border border-ink-chip bg-ink-surface px-3 py-1.5 text-meta text-ink-text hover:border-ink-muted"
              >
                Start / resume
              </button>
            }
          />
          <StatCard
            testId="catalog-card-import"
            title="Catalog import"
            subtitle={catalog?.job?.status}
            stats={catalog?.job?.progress && [
              { k: "Sets", v: catalog.job.progress.sets_processed ?? "—" },
              { k: "Cards", v: catalog.job.progress.cards_processed ?? "—" },
              { k: "Last run", v: (catalog.job.finished_at || catalog.job.updated_at || "—").slice(0, 10) },
            ]}
            worker={catalog?.job}
            actions={
              <button
                data-testid="catalog-sync-start"
                onClick={() => wrap(triggerCatalogSync, "Catalog import kicked off.")}
                className="rounded-card border border-ink-chip bg-ink-surface px-3 py-1.5 text-meta text-ink-text hover:border-ink-muted"
              >
                Run catalog sync
              </button>
            }
          />
        </div>

        {cache?.per_set && cache.per_set.length > 0 && (
          <div className="mt-8">
            <h2 className="text-title-section text-ink-text">Per-set cache completion</h2>
            <ul className="mt-3 space-y-1" data-testid="catalog-per-set">
              {cache.per_set.slice(0, 30).map((s) => (
                <li key={s.set_id} className="flex items-center justify-between rounded-chip border border-ink-chip bg-ink-surface px-3 py-2 text-meta">
                  <span className="text-ink-text truncate">{s.set_name || s.set_external_id}</span>
                  <span className="tabular text-ink-muted">
                    {s.cached} / {s.total} · {s.percent}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </RequireAdmin>
  );
}

function StatCard({ testId, title, subtitle, stats, worker, actions }) {
  return (
    <div
      data-testid={testId}
      className="rounded-card border border-ink-chip bg-ink-surface p-4"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-title-section text-ink-text">{title}</div>
        {subtitle && <div className="text-meta text-ink-muted tabular">{subtitle}</div>}
      </div>
      {stats && (
        <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-ink-chip pt-3">
          {stats.map((s) => (
            <div key={s.k}>
              <dt className="text-eyebrow uppercase text-ink-muted">{s.k}</dt>
              <dd className="mt-1 text-body text-ink-text tabular">{s.v}</dd>
            </div>
          ))}
        </dl>
      )}
      {worker && (
        <div className="mt-3 text-meta text-ink-muted tabular">
          worker: {worker.status || "?"}
          {worker.progress?.last_batch_cached != null &&
            ` · +${worker.progress.last_batch_cached} last batch`}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}
